import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { qualApi, qualErr, CARD, BTN } from "@/components/esriQual/esriQualApi";
import { QualStateBar } from "@/components/esriQual/QualStateBar";
import { ChecksList } from "@/components/esriQual/ChecksList";
import { SecretCard } from "@/components/esriQual/SecretCard";
import { AccountCard } from "@/components/esriQual/AccountCard";
import { EvidenceCard } from "@/components/esriQual/EvidenceCard";
import { ProbeCard } from "@/components/esriQual/ProbeCard";
import { ActivationCard } from "@/components/esriQual/ActivationCard";

function ReadinessCard({ q }) {
  return (
    <section data-testid="esri-readiness-card" className={CARD}>
      <h2 className="font-chivo text-base md:text-lg font-black uppercase tracking-tight">Contrôles préalables & readiness</h2>
      <ChecksList checks={q.prerequisites} testid="esri-prereq" />
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#525252]">Informatif (non bloquant pour les essais internes)</div>
      <ChecksList checks={[...q.contract, q.authorization]} testid="esri-contract" />
      <div className="font-mono text-[10px] space-y-0.5">
        <div>QUALIFICATION : <b data-testid="esri-readiness-qualification" className={q.readiness.QUALIFICATION.final === "GO" ? "text-[#22C55E]" : "text-[#EF4444]"}>{q.readiness.QUALIFICATION.final}</b> <span className="text-[#525252]">{q.readiness.QUALIFICATION.failed.join(", ")}</span></div>
        <div>COMMERCIAL : <b data-testid="esri-readiness-commercial" className="text-[#EF4444]">{q.readiness.COMMERCIAL.final}</b> <span className="text-[#525252]">(Issue #2 — conditions opérateurs)</span></div>
      </div>
    </section>
  );
}

function AuditCard({ rows }) {
  return (
    <section data-testid="esri-audit-card" className={CARD}>
      <h2 className="font-chivo text-base md:text-lg font-black uppercase tracking-tight">Journal (25 derniers)</h2>
      <ul className="font-mono text-[10px] text-[#A3A3A3] space-y-0.5 max-h-56 overflow-y-auto">
        {rows.map((r, i) => <li key={`${r.at}-${i}`} data-testid={`esri-audit-row-${i}`}><span className="text-[#525252]">{String(r.at).slice(0, 19)}</span> {r.action} · {r.user_email}</li>)}
      </ul>
    </section>
  );
}

export default function EsriQualification() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setData(await qualApi.load()); setError(null); } catch (e) { setError(qualErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-6 font-mono text-xs" data-testid="esri-qual-loading">{error || <Loader2 className="animate-spin" size={16} />}</div>;
  const { q, secret, esri, killSwitch, audit, evidence } = data;
  return (
    <div data-testid="esri-qualification-page" className="p-6 space-y-5 max-w-5xl">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl font-black tracking-tighter uppercase">Qualification Esri</h1>
          <p className="font-mono text-xs text-[#A3A3A3] mt-1">ArcGIS Static Basemap Tiles · essais internes · administrateurs uniquement</p>
        </div>
        <button data-testid="esri-qual-refresh-btn" onClick={load} className={BTN}><RefreshCw size={12} /> Actualiser</button>
      </div>
      <div className="flex items-start gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/10 rounded-sm p-3 font-mono text-[10px] text-[#F59E0B]">
        <ShieldAlert size={14} className="shrink-0" /> Pilotage XR : MOCK ONLY. Aucun appel Esri réel sans clic administrateur sur « Tester la connexion Esri ». Qualification matérielle Quest 3 : PENDING.
      </div>
      {error && <div data-testid="esri-qual-error" className="font-mono text-[10px] text-[#EF4444]">{error}</div>}
      <QualStateBar state={q.state} />
      <ReadinessCard q={q} />
      <SecretCard secret={secret} />
      <ProbeCard q={q} config={esri.config} onChange={load} />
      <ActivationCard q={q} esri={esri} killSwitch={killSwitch} onChange={load} />
      <AccountCard key={esri.config.token_expires_at || "none"} config={esri.config} onChange={load} />
      <EvidenceCard evidence={evidence} onChange={load} />
      <AuditCard rows={audit} />
    </div>
  );
}
